import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  FormControl,
  OutlinedInput,
  Stack,
  Link,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { makeStyles } from "@mui/styles";
import { Auth } from "aws-amplify";
import { Check, ArrowBack, ErrorOutline } from "@mui/icons-material";
import { CognitoUser } from "../../interfaces";
import { ReactComponent as BGLeft } from "../../assets/Login-BgLeft.svg";
import { ReactComponent as BGRight } from "../../assets/Login-BgRight.svg";
import { generateGtmEvent, LogoutUserLti } from "../../Helpers";
import { ButtonZoom } from "../../components/v2/utils/ButtonZoom";

const offsetStyle = "72px";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#303030",
    position: "relative",
    overflow: "hidden",
  },
  backgroundImage: {
    height: "100%",
    width: "auto",
    zIndex: 0,
    position: "absolute",
  },
  loginPanelContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  loginPanel: {
    zIndex: 100,
    width: "100%",
    maxWidth: 410,
    padding: 56,
  },
  inputLabel: {
    fontWeight: theme.typography.fontWeightBold,
  },
  inputField: {
    borderRadius: 8,
  },
  secondaryText: {
    color: "#787676",
    fontSize: "0.8750em",
  },
  errorIcon: {
    color: theme.palette.error.main,
    width: 16,
    height: 16,
    marginRight: 8,
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
  successIcon: {
    color: theme.palette.success.main,
    width: 16,
    height: 16,
    marginRight: 8,
  },
  successMessage: {
    color: theme.palette.success.main,
  },
  requirementsList: {
    listStyle: "none",
    paddingLeft: 0,
    margin: 0,
  },
  backToLoginButton: {
    width: 157,
    padding: 0,
    color: theme.palette.text.secondary,
    borderRadius: 8,
    "&:hover": {
      color: theme.palette.text.secondary,
      backgroundColor: "#F2F2F2",
      textDecoration: "underline",
    },
  },
  borderBottom: {
    width: "100%",
    border: "1px solid #D0D0D0",
  },
  faIcon: {
    marginRight: "8px",
  },
}));

enum PageModes {
  login = "login",
  resetPasswordRequired = "resetPasswordRequired", // first login after creation
  resetPasswordStart = "resetPasswordStart", // enter email to send code
  resetPasswordCode = "resetPasswordCode", // enter code and reset password
}

export const LoginPage = () => {
  const classes = useStyles();
  const history = useHistory();
  // general states
  const [pageMode, setPageMode] = useState<PageModes>(PageModes.login);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [successMessage, setSuccessMessage] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [currentCognitoUser, setCurrentCognitoUser] = useState<CognitoUser>();
  // login page states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // reset required page states
  const [requiredNewPassword, setRequiredNewPassword] = useState("");
  const [requiredConfirmPassword, setRequiredConfirmPassword] = useState("");
  // reset code entry page states
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const signIn = async (logUser: string, logPass: string) => {
    await Auth.signIn(logUser.toLowerCase(), logPass)
      .then(async (user) => {
        await LogoutUserLti();
        setCurrentCognitoUser(user);
      })
      .catch((error) => {
        if (error.message.includes("User is disabled")) {
          setErrorMessage(
            "Your account has been disabled. Please contact your administrator."
          );
        } else {
          setErrorMessage(error.message);
        }
        setLoading(false);
        // error exception handling
        if (error.name === "PasswordResetRequiredException") {
          setPageMode(PageModes.resetPasswordCode);
        }
      });
  };

  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearMessages();
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "log in to digit",
      text: "continue",
    });
    setLoading(true);
    await signIn(email, password);
  };

  const handleResetPasswordButton = async () => {
    clearMessages();
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "log in to digit",
      text: "reset password",
    });
    setPageMode(PageModes.resetPasswordStart);
  };

  const handleResetPasswordStartSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "reset password initiate",
      text: "continue",
    });

    await Auth.forgotPassword(email)
      .then((res) => {
        setPageMode(PageModes.resetPasswordCode);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      });

    setLoading(false);
  };

  const handleResetPasswordRequiredSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "reset password",
      text: "continue",
    });

    await Auth.completeNewPassword(currentCognitoUser, requiredNewPassword)
      .then(async (user) => {
        await signIn(email, requiredNewPassword);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      });

    setLoading(false);
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "reset password",
      text: "continue",
    });

    await Auth.forgotPasswordSubmit(email, verificationCode, newPassword)
      .then(async (res) => {
        //setPageMode(PageModes.resetPasswordCode);
        await signIn(email, newPassword);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      });

    setLoading(false);
  };

  const handleBackToLoginButton = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "reset password",
      text: "back to log in",
    });
    setPageMode(PageModes.login);
  };

  // reset error message on page mode change
  useEffect(() => {
    setErrorMessage(null);
    // retain success messages after redirecting to login
    if (pageMode !== PageModes.login) {
      setSuccessMessage(null);
    }
  }, [pageMode]);

  useEffect(() => {
    if (!currentCognitoUser) return;
    if (currentCognitoUser.challengeName === "NEW_PASSWORD_REQUIRED") {
      setLoading(false);
      // user's password is expired and must be reset
      setPageMode(PageModes.resetPasswordRequired);
    } else {
      // force application to re-load after successful auth
      history.go(0);
    }
  }, [currentCognitoUser, history]);

  const getHeader = () => {
    switch (pageMode) {
      case PageModes.resetPasswordRequired:
      case PageModes.resetPasswordStart:
      case PageModes.resetPasswordCode:
        return "Reset password";
      default:
        return "Log in to Digit";
    }
  };

  const header = getHeader();

  return (
    <Stack
      height={`calc(100vh - ${offsetStyle})`}
      minHeight={550}
      className={classes.root}
      id="main">
      <BGLeft className={classes.backgroundImage} style={{ left: "0", bottom: "0" }} />
      <BGRight className={classes.backgroundImage} style={{ right: "0", top: "0" }} />
      <Box className={classes.loginPanelContainer}>
        <Card className={classes.loginPanel}>
          <Typography variant="h2" component="h1" sx={{ marginBottom: "32px" }}>
            {header}
          </Typography>
          {successMessage && (
            <Stack direction="row" alignItems="center" sx={{ marginBottom: "32px" }}>
              <Check className={classes.successIcon} />
              <Typography variant="body1" className={classes.successMessage}>
                {successMessage}
              </Typography>
            </Stack>
          )}
          {errorMessage && (
            <Stack direction="row" alignItems="center" sx={{ marginBottom: "32px" }}>
              <ErrorOutline className={classes.errorIcon} />
              <Typography variant="body1" className={classes.errorMessage}>
                {errorMessage}
              </Typography>
            </Stack>
          )}
          {pageMode === PageModes.login && (
            <>
              <form onSubmit={handleLoginSubmit}>
                <FormControl fullWidth sx={{ marginBottom: "32px" }}>
                  <label htmlFor="login-email">
                    <Typography variant="body2" className={classes.inputLabel}>
                      Email
                    </Typography>
                  </label>
                  <OutlinedInput
                    name={"email"}
                    id="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "log in to digit ^ email",
                        text: e.target.value,
                      });
                      setEmail(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label htmlFor="login-password">
                    <Typography variant="body2" className={classes.inputLabel}>
                      Password
                    </Typography>
                  </label>
                  <OutlinedInput
                    name={"password"}
                    id="login-password"
                    type="password"
                    autoComplete="off"
                    required
                    value={password}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "log in to digit ^ password",
                        text: e.target.value.replace(/./g, "*"),
                      });
                      setPassword(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <Box sx={{ marginTop: "12px", marginBottom: "32px" }}>
                  <Typography variant="body2" className={classes.secondaryText}>
                    Forgot your password?{" "}
                    <Link onClick={handleResetPasswordButton}>Reset password</Link>.
                  </Typography>
                </Box>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    name={"login"}
                    color="primary"
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    loading={loading}>
                    Continue
                  </LoadingButton>
                </Stack>
                <Box
                  className={classes.borderBottom}
                  sx={{ marginTop: "24px", marginBottom: "24px" }}
                />
                <Stack direction="row" justifyContent="center">
                  <ButtonZoom
                    disableZoom={true}
                    size={"small"}
                    variant="outlined"
                    sx={{
                      width: "auto",
                      height: "50px",
                      borderRadius: "16px",
                      padding: "24px",
                      marginTop: "16px",
                      fontSize: "16px",
                      "&:hover": {
                        transform: "none",
                      },
                    }}
                    iconPosition="left"
                    icon={<i className={`fas fa-key ${classes.faIcon}`}></i>}
                    iconHover={<i className={`fas fa-key ${classes.faIcon}`}></i>}
                    onClick={() => {
                      history.push("/self-registration");
                    }}>
                    Use class code
                  </ButtonZoom>
                </Stack>
              </form>
            </>
          )}
          {pageMode === PageModes.resetPasswordRequired && (
            <>
              <form onSubmit={handleResetPasswordRequiredSubmit}>
                <FormControl fullWidth>
                  <label htmlFor="reset-newpassword">
                    <Typography variant="body2" className={classes.inputLabel}>
                      New Password
                    </Typography>
                  </label>
                  <OutlinedInput
                    id="reset-newpassword"
                    type="password"
                    required
                    value={requiredNewPassword}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "reset password ^ new password",
                        text: e.target.value.replace(/./g, "*"),
                      });
                      setRequiredNewPassword(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ marginTop: "12px" }}>
                  <label htmlFor="reset-confirmpassword">
                    <Typography variant="body2" className={classes.inputLabel}>
                      Confirm Password
                    </Typography>
                  </label>
                  <OutlinedInput
                    id="reset-confirmpassword"
                    type="password"
                    required
                    value={requiredConfirmPassword}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "reset password ^ confirm password",
                        text: e.target.value.replace(/./g, "*"),
                      });
                      setRequiredConfirmPassword(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <Box sx={{ marginTop: "12px" }}>
                  <Typography
                    variant="body2"
                    component="div"
                    className={classes.secondaryText}>
                    When setting a password, it must be:
                    <ul className={classes.requirementsList}>
                      <li>
                        A minimum of 8 characters Contain at least one lowercase letter
                        (a-z)
                      </li>
                      <li>Contain at least one uppercase letter (A-Z)</li>
                      <li>Contain a symbol (!, @, #, $, etc)</li>
                      <li>Contain a number (0-9)</li>
                    </ul>
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  justifyContent="center"
                  sx={{ marginTop: "20px", marginBottom: "12px" }}>
                  <Button
                    component={Link}
                    className={classes.backToLoginButton}
                    onClick={handleBackToLoginButton}>
                    <ArrowBack
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    Back to Log In
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    loading={loading}>
                    Continue
                  </LoadingButton>
                </Stack>
              </form>
            </>
          )}
          {pageMode === PageModes.resetPasswordStart && (
            <>
              <form onSubmit={handleResetPasswordStartSubmit}>
                <FormControl fullWidth>
                  <label htmlFor="login-email">
                    <Typography variant="body2" className={classes.inputLabel}>
                      Email
                    </Typography>
                  </label>
                  <OutlinedInput
                    id="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "reset password ^ email",
                        text: e.target.value,
                      });
                      setEmail(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <Stack
                  direction="row"
                  justifyContent="center"
                  sx={{ marginTop: "20px", marginBottom: "12px" }}>
                  <Button
                    component={Link}
                    className={classes.backToLoginButton}
                    onClick={handleBackToLoginButton}>
                    <ArrowBack
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    Back to Log In
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    loading={loading}>
                    Continue
                  </LoadingButton>
                </Stack>
              </form>
            </>
          )}
          {pageMode === PageModes.resetPasswordCode && (
            <>
              <form onSubmit={handleResetPasswordSubmit}>
                <FormControl fullWidth sx={{ marginBottom: "32px" }}>
                  <label htmlFor="reset-verificationcode">
                    <Typography variant="body2" className={classes.inputLabel}>
                      Verification code
                    </Typography>
                  </label>
                  <OutlinedInput
                    id="reset-verificationcode"
                    type="text"
                    required
                    value={verificationCode}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "reset password ^ verification code",
                        text: e.target.value.replace(/./g, "*"),
                      });
                      setVerificationCode(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label htmlFor="reset-newpassword">
                    <Typography variant="body2" className={classes.inputLabel}>
                      New Password
                    </Typography>
                  </label>
                  <OutlinedInput
                    id="reset-newpassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => {
                      generateGtmEvent({
                        event: "form",
                        action: "click",
                        name: "onclick",
                        type: "blur",
                        region: "main content",
                        section: "reset password ^ new password",
                        text: e.target.value.replace(/./g, "*"),
                      });
                      setNewPassword(e.target.value);
                    }}
                    className={classes.inputField}
                  />
                </FormControl>
                <Box sx={{ marginTop: "12px" }}>
                  <Typography
                    variant="body2"
                    component="div"
                    className={classes.secondaryText}>
                    When setting a password, it must be:
                    <ul className={classes.requirementsList}>
                      <li>
                        A minimum of 8 characters Contain at least one lowercase letter
                        (a-z)
                      </li>
                      <li>Contain at least one uppercase letter (A-Z)</li>
                      <li>Contain a symbol (!, @, #, $, etc)</li>
                      <li>Contain a number (0-9)</li>
                    </ul>
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  justifyContent="center"
                  sx={{ marginTop: "20px", marginBottom: "12px" }}>
                  <Button
                    component={Link}
                    className={classes.backToLoginButton}
                    onClick={handleBackToLoginButton}>
                    <ArrowBack
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    Back to Log In
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="center">
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    loading={loading}>
                    Continue
                  </LoadingButton>
                </Stack>
              </form>
            </>
          )}
        </Card>
      </Box>
    </Stack>
  );
};
